/* eslint-disable react/react-in-jsx-scope */
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { useAuth } from "@/auth/hooks/useAuth";
import IconExcel from "@/assets/icones/excel.svg?react";
import IconImg from "@/assets/icones/images.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  buildDocumentTree,
  DocumentFolderTreeResponse,
  DocumentRecord,
  DocumentTreeNode,
  getDocumentCategoryInfo,
  getDocumentTreeSearchEntries,
  getDocumentTreePathLabel,
} from "@/documents/utils/documentTree";
import { useMutateData } from "@/hooks/useMutation";
import usePostData from "@/hooks/usePostData";
import { useQueryClient } from "@tanstack/react-query";
import { TreeRoot } from "@/types";
import { Download, FolderOpen, Info, Link, Loader2, MoreHorizontal, Pencil, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import { toast } from "@/components/ui/use-toast";
import { copyDocumentLink } from "@/documents/utils/copyDocumentLink";
import { downloadDocumentFile } from "@/documents/utils/downloadDocumentFile";
import { downloadDocumentFolder } from "@/documents/utils/downloadDocumentFolder";
import {
  showDownloadErrorToast,
  showDownloadInProgressToast,
  showDownloadSuccessToast,
} from "@/documents/utils/downloadToast";
import DocumentCard from "./DocumentCard";
import { DocumentDetailsContent } from "./DocumentDetailsDialog";
import DocumentList from "./DocumentList";
import { FolderDetailsContent } from "./FolderDetailsDialog";
import ImageThumbnail from "./ImageThumbnail";
import OfficeThumbnail from "./OfficeThumbnail";
import PdfThumbnail from "./PdfThumbnail";

type ExplorerDocument = DocumentRecord & {
  at?: {
    date?: string;
  } | string | null;
  createdAt?: string;
};

type Props = {
  data: ExplorerDocument[];
  carrierTree?: TreeRoot[];
  customFolders?: DocumentFolderTreeResponse;
  currentUserId?: number | null;
  searchParams: URLSearchParams;
  breadcrumbItems?: Array<{ name: string; href?: string }>;
  onDelete?: (id: number) => void | Promise<void>;
  filesView?: "folder" | "list" | "card";
  typesDocs?: any[];
  filesToolbar?: ReactNode;
  stayOnCurrentPath?: boolean;
  lockedSearchParams?: Record<string, string | number | null | undefined>;
};

type TreeNodeWithPath = DocumentTreeNode & {
  fullPath: string;
};

type FileToDelete = {
  id: number;
  name?: string;
};

type DocumentsCacheSnapshot = Array<{
  queryKey: readonly unknown[];
  data: unknown;
}>;

const joinPath = (parentPath: string, label: string) =>
  parentPath ? `${parentPath} / ${label}` : label;

const getCustomFolderNodeId = (node: DocumentTreeNode): number | null =>
  node.deleteFolderTarget?.kind === "custom" && node.deleteFolderTarget.id
    ? Number(node.deleteFolderTarget.id)
    : null;

const getCustomFolderNodeIds = (node: DocumentTreeNode): number[] => [
  getCustomFolderNodeId(node),
  ...(node.children ?? []).flatMap(getCustomFolderNodeIds),
].filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0);

const normalizeDocumentTypeIds = (value: string | null) =>
  (value ?? "")
    .split(",")
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0);

const PROJECT_DOC_TYPES = [2, 3, 7, 8];
const ROOT_FILES_DEFAULT_KEY = "root-files";
const PROGRAM_ROOT_DEFAULT_KEY_PREFIX = "program-";

const parseNumericParam = (value: string | null) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const getLinkedFolders = (doc: ExplorerDocument): Array<{
  id?: number | null;
  defaultKey?: string | null;
  scopeType?: string | null;
  scopeId?: number | null;
}> =>
  Array.isArray((doc as any).documentFolders) ? (doc as any).documentFolders : [];

const canRenameFolder = (folder: TreeNodeWithPath) => {
  const target = folder.deleteFolderTarget;

  if (!target) {
    return false;
  }

  if (
    target.kind === "default" &&
    target.scopeType === "porteur" &&
    target.defaultKey?.startsWith(PROGRAM_ROOT_DEFAULT_KEY_PREFIX)
  ) {
    return false;
  }

  if (
    target.kind === "default" &&
    target.scopeType === "project" &&
    target.defaultKey === "root"
  ) {
    return false;
  }

  return true;
};

const getLinkedFolderIds = (doc: ExplorerDocument) => {
  const folderIds = Array.isArray((doc as any).documentFolderIds)
    ? (doc as any).documentFolderIds
    : [];

  return [
    ...getLinkedFolders(doc).map((folder) => Number(folder.id ?? 0)),
    ...folderIds.map((folderId: any) => Number(folderId)),
  ].filter((folderId) => Number.isFinite(folderId) && folderId > 0);
};

const findFolderPathById = (
  folders: NonNullable<DocumentFolderTreeResponse["porteur"]>[string] | undefined,
  folderId: number,
): Array<{
  id: number;
  defaultKey?: string | null;
  children?: any[];
}> | null => {
  if (!Array.isArray(folders)) {
    return null;
  }

  for (const folder of folders) {
    if (Number(folder?.id) === folderId) {
      return [folder];
    }

    const nested = findFolderPathById(folder?.children as any, folderId);
    if (nested) {
      return [folder, ...nested];
    }
  }

  return null;
};

const getDocumentSubtype = (doc: ExplorerDocument): number | null => {
  const candidate = (doc as any).subType ?? (doc as any).idSubcategory ?? doc.type;
  const numeric = Number(candidate);
  return Number.isFinite(numeric) ? numeric : null;
};

const getFileIcon = (name?: string | null) => {
  const extension = name?.split(".").pop()?.toLowerCase() ?? "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension) || name?.toLowerCase() === "blob") {
    return <IconImg width={22} height={22} />;
  }

  if (["xls", "xlsx", "ods", "csv"].includes(extension)) {
    return <IconExcel width={22} height={22} />;
  }

  if (["doc", "docx", "txt", "odt", "rtf"].includes(extension)) {
    return <IconWord width={22} height={22} />;
  }

  return <IconPdf width={22} height={22} />;
};

const isImageFile = (name?: string | null) => {
  const extension = name?.split(".").pop()?.toLowerCase() ?? "";
  return name?.toLowerCase() === "blob" || ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension);
};

const isOfficeFile = (name?: string | null) => {
  const extension = name?.split(".").pop()?.toLowerCase() ?? "";
  return ["doc", "docx", "xlsx", "ods"].includes(extension);
};

const formatDocDate = (doc: ExplorerDocument) => {
  const rawDate =
    typeof doc.at === "string" ? doc.at : doc.at?.date ?? doc.createdAt ?? null;

  if (!rawDate) return "Sans date";

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "Sans date";

  return parsed.toLocaleDateString(
    "fr-FR",
    (doc as any).datePrecision === "month"
      ? {
          month: "long",
          year: "numeric",
        }
      : {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        },
  );
};

const removeDocumentFromCollection = (collection: unknown, id: number): unknown => {
  if (Array.isArray(collection)) {
    return collection.filter((doc: any) => Number(doc?.id) !== Number(id));
  }

  if (collection && typeof collection === "object") {
    return Object.fromEntries(
      Object.entries(collection).map(([key, value]) => [
        key,
        Array.isArray(value)
          ? value.filter((doc: any) => Number(doc?.id) !== Number(id))
          : value,
      ]),
    );
  }

  return collection;
};

const removeFolderFromCollection = (collection: unknown, folderId: number): unknown => {
  if (Array.isArray(collection)) {
    return collection
      .filter((folder: any) => Number(folder?.id) !== Number(folderId))
      .map((folder: any) => ({
        ...folder,
        children: removeFolderFromCollection(folder?.children ?? [], folderId),
      }));
  }

  if (collection && typeof collection === "object") {
    return Object.fromEntries(
      Object.entries(collection).map(([key, value]) => [
        key,
        key === "hiddenDefaults" ? value : removeFolderFromCollection(value, folderId),
      ]),
    );
  }

  return collection;
};

const renameFolderInCollection = (collection: unknown, folderId: number, name: string): unknown => {
  if (Array.isArray(collection)) {
    return collection.map((folder: any) => ({
      ...folder,
      ...(Number(folder?.id) === Number(folderId) ? { name } : {}),
      children: renameFolderInCollection(folder?.children ?? [], folderId, name),
    }));
  }

  if (collection && typeof collection === "object") {
    return Object.fromEntries(
      Object.entries(collection).map(([key, value]) => [
        key,
        key === "hiddenDefaults" ? value : renameFolderInCollection(value, folderId, name),
      ]),
    );
  }

  return collection;
};

const DocumentFolderExplorer = ({
  data,
  carrierTree,
  customFolders,
  currentUserId,
  searchParams,
  breadcrumbItems = [],
  onDelete,
  filesView = "folder",
  typesDocs = [],
  filesToolbar,
  stayOnCurrentPath = false,
  lockedSearchParams,
}: Props) => {
  const { user } = useAuth();
  const hidePostActions = useHidePostActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { postData } = usePostData<any>();
  const [folderToRename, setFolderToRename] = useState<TreeNodeWithPath | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<TreeNodeWithPath | null>(null);
  const [folderToShowDetails, setFolderToShowDetails] = useState<TreeNodeWithPath | null>(null);
  const [documentToShowDetails, setDocumentToShowDetails] = useState<ExplorerDocument | null>(null);
  const [fileToDelete, setFileToDelete] = useState<FileToDelete | null>(null);
  const [deleteRedirectUrl, setDeleteRedirectUrl] = useState<string | null>(null);
  const [isDeletingDefaultFolderWithFiles, setIsDeletingDefaultFolderWithFiles] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [hasAccessToEdit, setHasAccessToEdit] = useState(true);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);
  const [downloadingFolderId, setDownloadingFolderId] = useState<string | null>(null);
  const explorerLayoutRef = useRef<HTMLDivElement | null>(null);
  const [explorerLayoutHeight, setExplorerLayoutHeight] = useState<string>("520px");
  const [optimisticallyDeletedDocumentIds, setOptimisticallyDeletedDocumentIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [optimisticFolderLabels, setOptimisticFolderLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setHasAccessToEdit(
        Boolean(
          user?.roles?.includes("ROLE_CARRIER") ||
          user?.roles?.includes("ROLE_CPINTERVENANT") ||
          (user?.roles?.includes("ROLE_SUBCONTRACTOR") && user?.fullAccess),
        ),
      );
    }
  }, [user]);

  const openFolder = (url?: string) => {
    const targetUrl = url ?? "/dashboard/mes-documents";
    const target = new URL(targetUrl, window.location.origin);
    const targetSearchParams = new URLSearchParams(target.searchParams);

    Object.entries(lockedSearchParams ?? {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        targetSearchParams.delete(key);
        return;
      }

      targetSearchParams.set(key, String(value));
    });

    if (stayOnCurrentPath) {
      ["tab", "step"].forEach((key) => {
        const currentValue = new URLSearchParams(window.location.search).get(key);
        if (currentValue && !targetSearchParams.get(key)) {
          targetSearchParams.set(key, currentValue);
        }
      });

      setSearchParams(targetSearchParams, { replace: false });
      return;
    }

    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const nextUrl = `${target.pathname}${targetSearchParams.toString() ? `?${targetSearchParams.toString()}` : ""}`;
    if (currentUrl === nextUrl) {
      return;
    }

    if (target.pathname === location.pathname) {
      setSearchParams(targetSearchParams, { replace: false });
      return;
    }
    navigate(nextUrl);
  };

  const nodes = useMemo(
    () => {
      const isCarrier = !!user?.roles?.includes("ROLE_CARRIER");
      const isSubcontractor = !!user?.roles?.includes("ROLE_SUBCONTRACTOR");

      return buildDocumentTree({
        documentsData: { current: data },
        carrierTree,
        customFolders,
        currentUserId,
        activeProgramId: parseNumericParam(searchParams.get("programs")?.split(",")[0] ?? null),
        activeProjectId: parseNumericParam(searchParams.get("projects")?.split(",")[0] ?? null),
        activeIntervenantId: parseNumericParam(searchParams.get("intervenants")?.split(",")[0] ?? null),
        hideIntervenantFolder: isSubcontractor && !isCarrier,
        directIntervenantsOnly: isSubcontractor && isCarrier,
      });
    },
    [carrierTree, currentUserId, customFolders, data, searchParams, user?.roles],
  );

  const currentEntries = useMemo(
    () => getDocumentTreeSearchEntries(searchParams),
    [searchParams],
  );
  const isDocumentsRootView =
    !(searchParams.get("scope") ?? "") &&
    !(searchParams.get("programs") ?? "") &&
    !(searchParams.get("projects") ?? "") &&
    !(searchParams.get("folderIds") ?? "") &&
    !(searchParams.get("intervenants") ?? "") &&
    !(searchParams.get("origin") ?? "");
  const hasExplicitDocumentLocation = !isDocumentsRootView;

  const isSelected = (url?: string) => {
    if (!url) return false;
    const [, query = ""] = url.split("?");
    const targetParams = new URLSearchParams(query);
    const currentParams = new URLSearchParams(searchParams.toString());

    if (
      currentParams.get("origin") === "PROGRAM" &&
      !targetParams.get("origin") &&
      (targetParams.get("programs") || targetParams.get("projects"))
    ) {
      currentParams.delete("origin");
    }

    const targetEntries = getDocumentTreeSearchEntries(targetParams);
    const normalizedCurrentEntries = getDocumentTreeSearchEntries(currentParams);
    return JSON.stringify(targetEntries) === JSON.stringify(normalizedCurrentEntries);
  };

  const treeContext = useMemo(() => {
    const applyOptimisticNode = (item: DocumentTreeNode, parentPath: string): TreeNodeWithPath => {
      const optimisticLabel = optimisticFolderLabels[item.id];

      return {
        ...item,
        label: optimisticLabel ?? item.label,
        fullPath: joinPath(parentPath, optimisticLabel ?? item.label),
      };
    };

    const walk = (
      items: DocumentTreeNode[],
      parentPath = "",
      parents: TreeNodeWithPath[] = [],
    ): {
      selectedNode: TreeNodeWithPath | null;
      selectedParents: TreeNodeWithPath[];
      currentChildren: TreeNodeWithPath[];
      flatNodes: TreeNodeWithPath[];
    } => {
      const flatNodes: TreeNodeWithPath[] = [];

      for (const item of items) {
        if (optimisticFolderLabels[item.id] === "") {
          continue;
        }

        const optimisticLabel = optimisticFolderLabels[item.id];
        const withPath: TreeNodeWithPath = {
          ...item,
          label: optimisticLabel ?? item.label,
          fullPath: joinPath(parentPath, optimisticLabel ?? item.label),
        };
        flatNodes.push(withPath);

        const nextParents = [...parents, withPath];
        const childrenResult = walk(item.children ?? [], withPath.fullPath, nextParents);
        flatNodes.push(...childrenResult.flatNodes);

        if (isSelected(item.url)) {
          return {
            selectedNode: withPath,
            selectedParents: parents,
            currentChildren: (item.children ?? [])
              .filter((child) => optimisticFolderLabels[child.id] !== "")
              .map((child) => applyOptimisticNode(child, withPath.fullPath)),
            flatNodes,
          };
        }

        if (childrenResult.selectedNode) {
          return {
            selectedNode: childrenResult.selectedNode,
            selectedParents: childrenResult.selectedParents,
            currentChildren: childrenResult.currentChildren,
            flatNodes,
          };
        }
      }

      return {
        selectedNode: null,
        selectedParents: [],
          currentChildren: items
            .filter((item) => optimisticFolderLabels[item.id] !== "")
            .map((item) => applyOptimisticNode(item, parentPath)),
          flatNodes,
      };
    };

    const result = walk(nodes);

    if (
      hasExplicitDocumentLocation &&
      result.selectedNode === null &&
      currentEntries.length > 0
    ) {
      return {
        ...result,
        currentChildren: [],
      };
    }

    return result;
  }, [currentEntries, hasExplicitDocumentLocation, nodes, optimisticFolderLabels]);

  const documentsWithPath = useMemo(
    () =>
      data
        .filter((doc) => !optimisticallyDeletedDocumentIds.has(Number(doc.id)))
        .map((doc) => ({
          ...doc,
          ...getDocumentCategoryInfo(typesDocs, doc),
          treePath: getDocumentTreePathLabel({
            doc,
            carrierTree,
            customFolders,
            currentUserId,
          }),
        })),
    [carrierTree, currentUserId, customFolders, data, optimisticallyDeletedDocumentIds, typesDocs],
  );

  const folderCards = useMemo(
    () =>
      treeContext.currentChildren.map((child) => {
        const customFolderIds = getCustomFolderNodeIds(child);
        const filesCount = documentsWithPath.filter((doc) => {
          const matchesFolderId =
            customFolderIds.length > 0 &&
            getLinkedFolderIds(doc).some((folderId) => customFolderIds.includes(folderId));
          const matchesPath =
            doc.treePath === child.fullPath ||
            doc.treePath.startsWith(`${child.fullPath} / `);

          return matchesFolderId || matchesPath;
        }).length;
        const directFoldersCount = (child.children ?? []).length;
        const count = filesCount + directFoldersCount;

        return {
          ...child,
          count,
        };
      }),
    [documentsWithPath, treeContext.currentChildren],
  );

  const currentScopePath = useMemo(() => {
    if (treeContext.selectedNode?.fullPath) {
      return treeContext.selectedNode.fullPath;
    }

    const labels = breadcrumbItems
      .map((item) => item.name)
      .filter(Boolean)
      .filter((label) => label !== "Mes documents");

    if (labels.length > 0) {
      return labels.join(" / ");
    }

    return "";
  }, [breadcrumbItems, treeContext.selectedNode]);

  const visibleFiles = useMemo(() => {
    const selectedProjectId = parseNumericParam(searchParams.get("projects"));
    const selectedProgramId = parseNumericParam(searchParams.get("programs"));
    const selectedIntervenantId = parseNumericParam(searchParams.get("intervenants"));
    const selectedFolderId = parseNumericParam(searchParams.get("folderIds"));
    const selectedCategoryIds = normalizeDocumentTypeIds(searchParams.get("categories"));
    const selectedTypes = (searchParams.get("types") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const selectedScope = searchParams.get("scope");
    const visibleDefaultChildKeys = new Set(
      (treeContext.selectedNode?.children ?? [])
        .map((child) => child.deleteFolderTarget?.defaultKey ?? null)
        .filter((value): value is string => Boolean(value)),
    );

    const isDefaultParentRootView =
      null === selectedFolderId
      && selectedCategoryIds.length === 0
      && selectedTypes.length === 0;

    const isRootView =
      !currentScopePath &&
      !selectedProjectId &&
      !selectedProgramId &&
      !selectedIntervenantId &&
      !selectedFolderId &&
      selectedCategoryIds.length === 0 &&
      selectedTypes.length === 0 &&
      !selectedScope &&
      !(searchParams.get("origin") ?? "");

    const isLinkedToRootFilesFolder = (doc: ExplorerDocument) =>
      getLinkedFolders(doc)
        .filter((folder) => folder.scopeType === "porteur" && Number(folder.id ?? 0) > 0)
        .some((folder) => {
          const folderPath = findFolderPathById(
            customFolders?.porteur?.[String(currentUserId ?? 0)],
            Number(folder.id),
          );

          return (
            (folderPath?.length ?? 0) === 1 &&
            (folderPath?.[0]?.defaultKey ?? null) === ROOT_FILES_DEFAULT_KEY
          );
        });

    const selectedProjectDefaultKey =
      selectedProjectId && !selectedFolderId && selectedTypes.includes("image")
        ? "photos"
        : selectedProjectId &&
            !selectedFolderId &&
            selectedCategoryIds.length === 1 &&
            PROJECT_DOC_TYPES.includes(selectedCategoryIds[0]!)
          ? `type-${selectedCategoryIds[0]}`
          : null;

    const isLinkedDirectlyToCurrentProjectDefaultFolder = (doc: ExplorerDocument) => {
      if (!selectedProjectId || !selectedProjectDefaultKey) {
        return false;
      }

      return getLinkedFolders(doc)
        .filter(
          (folder) =>
            folder.scopeType === "project" &&
            Number(folder.scopeId ?? 0) === selectedProjectId &&
            Number(folder.id ?? 0) > 0,
        )
        .some((folder) => {
          if (folder.defaultKey === selectedProjectDefaultKey) {
            return true;
          }

          const folderPath = findFolderPathById(
            customFolders?.project?.[String(selectedProjectId)],
            Number(folder.id),
          );

          return (
            (folderPath?.length ?? 0) === 1 &&
            (folderPath?.[0]?.defaultKey ?? null) === selectedProjectDefaultKey
          );
        });
    };

    const isCurrentProjectDefaultDocument = (doc: ExplorerDocument) => {
      if (!selectedProjectId || !selectedProjectDefaultKey || Number(doc.project ?? 0) !== selectedProjectId) {
        return false;
      }

      if (selectedProjectDefaultKey === "photos") {
        return isImageFile(doc.name);
      }

      const selectedCategoryId = Number(selectedProjectDefaultKey.replace("type-", ""));
      return Number.isFinite(selectedCategoryId) && getDocumentSubtype(doc) === selectedCategoryId;
    };

    const belongsToCurrentDefaultParent = (doc: ExplorerDocument) => {
      const linkedFolders = getLinkedFolders(doc);
      const subtype = getDocumentSubtype(doc);
      const porteurFolderPaths = linkedFolders
        .filter((folder) => folder.scopeType === "porteur" && Number(folder.id ?? 0) > 0)
        .map((folder) => {
          return findFolderPathById(
            customFolders?.porteur?.[String(currentUserId ?? 0)],
            Number(folder.id),
          );
        })
        .filter((folderPath): folderPath is NonNullable<typeof folderPath> => Boolean(folderPath));

      const isLinkedDirectlyToPorteurDefault = (defaultKey: string) =>
        porteurFolderPaths.some(
          (folderPath) =>
            folderPath.length === 1 &&
            (folderPath[0]?.defaultKey ?? null) === defaultKey,
        );

      if (selectedProjectId) {
        const projectLinkedFolders = linkedFolders.filter(
          (folder) =>
            folder.scopeType === "project" &&
            Number(folder.scopeId ?? 0) === selectedProjectId,
        );
        const isMatchingProject =
          Number(doc.project ?? 0) === selectedProjectId ||
          projectLinkedFolders.length > 0;

        if (!isMatchingProject) {
          return false;
        }

        if (linkedFolders.length > 0) {
          return projectLinkedFolders.some((folder) => {
            if (folder.defaultKey === "root") {
              return true;
            }

            if (!Number(folder.id ?? 0)) {
              return false;
            }

            const folderPath = findFolderPathById(
              customFolders?.project?.[String(selectedProjectId)],
              Number(folder.id),
            );

            return (
              (folderPath?.length ?? 0) === 1 &&
              (folderPath?.[0]?.defaultKey ?? null) === "root"
            );
          });
        }

        if (visibleDefaultChildKeys.has("photos") && isImageFile(doc.name)) {
          return false;
        }

        if (
          subtype !== null &&
          PROJECT_DOC_TYPES.includes(subtype) &&
          visibleDefaultChildKeys.has(`type-${subtype}`)
        ) {
          return false;
        }

        return true;
      }

      if (selectedProgramId) {
        return isLinkedDirectlyToPorteurDefault(`program-${selectedProgramId}`);
      }

      if (selectedIntervenantId) {
        const intervenantLinkedFolders = linkedFolders.filter(
          (folder) =>
            folder.scopeType === "intervenant" &&
            Number(folder.scopeId ?? 0) === selectedIntervenantId,
        );
        const isMatchingIntervenant =
          intervenantLinkedFolders.length > 0 ||
          (Array.isArray(doc.intervenants)
          && doc.intervenants.some(
            (intervenant) => Number(intervenant?.id ?? 0) === selectedIntervenantId,
          ));

        if (!isMatchingIntervenant) {
          return false;
        }

        if (linkedFolders.length === 0) {
          return true;
        }

        return intervenantLinkedFolders.some(
          (folder) => {
            if (folder.defaultKey === "root") {
              return true;
            }

            if (
              !Number(folder.id ?? 0)
            ) {
              return false;
            }

            const folderPath = findFolderPathById(
              customFolders?.intervenant?.[String(selectedIntervenantId)],
              Number(folder.id),
            );

            return (
              (folderPath?.length ?? 0) === 1 &&
              (folderPath?.[0]?.defaultKey ?? null) === "root"
            );
          },
        );
      }

      if (selectedScope === "projects") {
        return isLinkedDirectlyToPorteurDefault("projects-root");
      }

      if (selectedScope === "intervenants") {
        return isLinkedDirectlyToPorteurDefault("intervenants-root");
      }

      if (selectedScope === "porteur") {
        if (
          !doc.project
          && !doc.program
          && (!Array.isArray(doc.intervenants) || doc.intervenants.length === 0)
          && linkedFolders.length === 0
        ) {
          return true;
        }

        return isLinkedDirectlyToPorteurDefault("root");
      }

      return false;
    };

    if (isRootView) {
      return documentsWithPath.filter(isLinkedToRootFilesFolder);
    }

    if (!currentScopePath) return [];

    return documentsWithPath.filter(
      (doc) => {
        if (selectedFolderId) {
          return getLinkedFolderIds(doc).includes(selectedFolderId);
        }

        if (doc.treePath === currentScopePath) {
          return true;
        }

        if (isCurrentProjectDefaultDocument(doc)) {
          return true;
        }

        if (isLinkedDirectlyToCurrentProjectDefaultFolder(doc)) {
          return true;
        }

        if (!isDefaultParentRootView) {
          return false;
        }

        return belongsToCurrentDefaultParent(doc);
      },
    );
  }, [currentScopePath, currentUserId, customFolders, documentsWithPath, searchParams, treeContext.selectedNode]);

  const parentFolderUrl = useMemo(() => {
    const selectedParents = treeContext.selectedParents;

    if (selectedParents.length > 0) {
      return selectedParents[selectedParents.length - 1]?.url ?? "/dashboard/mes-documents";
    }

    const navigableBreadcrumbs = breadcrumbItems.filter((item) => Boolean(item.href));
    if (navigableBreadcrumbs.length > 0) {
      return navigableBreadcrumbs[navigableBreadcrumbs.length - 1]?.href ?? "/dashboard/mes-documents";
    }

    return null;
  }, [breadcrumbItems, treeContext.selectedParents]);

  const refreshDocumentTreeQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "fetchDataV2" &&
        typeof query.queryKey[1] === "string" &&
        (query.queryKey[1].includes("/document-folders/tree") ||
          query.queryKey[1].includes("/docs/carrier")),
    });
  };

  const refreshDocumentQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "fetchDataV2" &&
        typeof query.queryKey[1] === "string" &&
        query.queryKey[1].includes("/docs/carrier"),
    });
  };

  const removeDocumentFromQueries = (id: number): DocumentsCacheSnapshot => {
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "fetchDataV2" &&
        typeof query.queryKey[1] === "string" &&
        query.queryKey[1].includes("/docs/carrier"),
    });

    const snapshots = queries.map((query) => ({
      queryKey: query.queryKey,
      data: queryClient.getQueryData(query.queryKey),
    }));

    queries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (previous: unknown) =>
        removeDocumentFromCollection(previous, id),
      );
    });

    return snapshots;
  };

  const restoreDocumentQueries = (snapshots: DocumentsCacheSnapshot) => {
    snapshots.forEach((snapshot) => {
      queryClient.setQueryData(snapshot.queryKey, snapshot.data);
    });
  };

  const getDocumentTreeSnapshots = (): DocumentsCacheSnapshot => {
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "fetchDataV2" &&
        typeof query.queryKey[1] === "string" &&
        query.queryKey[1].includes("/document-folders/tree"),
    });

    return queries.map((query) => ({
      queryKey: query.queryKey,
      data: queryClient.getQueryData(query.queryKey),
    }));
  };

  const restoreDocumentTreeQueries = (snapshots: DocumentsCacheSnapshot) => {
    snapshots.forEach((snapshot) => {
      queryClient.setQueryData(snapshot.queryKey, snapshot.data);
    });
  };

  const removeFolderFromTreeQueries = (folderId: number) => {
    queryClient.setQueriesData(
      {
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "fetchDataV2" &&
          typeof query.queryKey[1] === "string" &&
          query.queryKey[1].includes("/document-folders/tree"),
      },
      (previous: unknown) => removeFolderFromCollection(previous, folderId),
    );
  };

  const renameFolderInTreeQueries = (folderId: number, name: string) => {
    queryClient.setQueriesData(
      {
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "fetchDataV2" &&
          typeof query.queryKey[1] === "string" &&
          query.queryKey[1].includes("/document-folders/tree"),
      },
      (previous: unknown) => renameFolderInCollection(previous, folderId, name),
    );
  };

  const { mutateAsync: saveFolderAsync, isPending: isSavingFolder } = useMutateData({
    id: "",
    onSuccess: async () => {
      await refreshDocumentTreeQueries();
      setFolderToRename(null);
      setRenameValue("");
    },
  });
  const { mutateAsync: deleteMediaAsync, isPending: isDeletingMedia } = useMutateData({
    id: "",
    onSuccess: async () => {
      await refreshDocumentTreeQueries();
      setFileToDelete(null);
    },
  });

  const { mutateAsync: deleteFolderAsync, isPending: isDeletingFolder } = useMutateData({
    id: "",
    onSuccess: async () => {
      const deletedFolderId =
        folderToDelete?.deleteFolderTarget?.kind === "custom"
          ? Number(folderToDelete.deleteFolderTarget.id ?? 0)
          : 0;
      if (deletedFolderId > 0) {
        removeFolderFromTreeQueries(deletedFolderId);
      }
      toast({
        title: "Dossier supprimé",
        description: "Le dossier a été retiré de vos documents.",
        duration: 3500,
      });
      setFolderToDelete(null);
      setIsDeletingDefaultFolderWithFiles(false);
      if (deleteRedirectUrl) {
        openFolder(deleteRedirectUrl);
      }
      setDeleteRedirectUrl(null);
      await refreshDocumentTreeQueries();
    },
  });
  const isFolderDeleteInProgress =
    isDeletingFolder || isDeletingMedia || isSavingFolder || isDeletingDefaultFolderWithFiles;

  const hideDefaultFolder = async (folder: TreeNodeWithPath) => {
    if (folder.deleteFolderTarget?.kind !== "default" || !folder.deleteFolderTarget.defaultKey) {
      return;
    }

    await saveFolderAsync({
      endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
      body: {
        scopeType: folder.deleteFolderTarget.scopeType,
        scopeId: folder.deleteFolderTarget.scopeId,
        defaultKey: folder.deleteFolderTarget.defaultKey,
      },
    });
  };

  const getFolderMediaIds = (folder: TreeNodeWithPath) =>
    Array.from(new Set(
      documentsWithPath
        .filter((doc) => doc.treePath === folder.fullPath || doc.treePath.startsWith(`${folder.fullPath} / `))
        .map((doc) => Number(doc.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ));

  const getFolderDocuments = (folder: TreeNodeWithPath) =>
    documentsWithPath.filter(
      (doc) => doc.treePath === folder.fullPath || doc.treePath.startsWith(`${folder.fullPath} / `),
    );

  const getFolderStats = (folder: TreeNodeWithPath | null) => ({
    fileCount: folder ? getFolderDocuments(folder).length : 0,
    folderCount: folder?.children?.length ?? 0,
  });

  const handleDeleteFolder = async (
    folder: TreeNodeWithPath,
    options: { deleteFiles?: boolean } = {},
  ) => {
    if (!folder.deleteFolderTarget) return;
    const shouldRedirectAfterDelete = Boolean(folder.url) && isSelected(folder.url);
    const redirectUrl = shouldRedirectAfterDelete
      ? parentFolderUrl ?? "/dashboard/mes-documents"
      : null;
    setDeleteRedirectUrl(
      redirectUrl,
    );

    if (folder.deleteFolderTarget.kind === "custom" && folder.deleteFolderTarget.id) {
      const folderId = Number(folder.deleteFolderTarget.id);
      const treeSnapshots = getDocumentTreeSnapshots();
      if (folderId > 0) {
        removeFolderFromTreeQueries(folderId);
      }
      setFolderToDelete(null);
      if (redirectUrl) {
        openFolder(redirectUrl);
      }

      try {
        await deleteFolderAsync({
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/delete/${folder.deleteFolderTarget.id}`,
          body: {},
        });
      } catch (error) {
        restoreDocumentTreeQueries(treeSnapshots);
      }
      return;
    }

    if (folder.deleteFolderTarget.kind === "default" && folder.deleteFolderTarget.defaultKey) {
      const previousOptimisticLabel = optimisticFolderLabels[folder.id];
      setOptimisticFolderLabels((prev) => ({
        ...prev,
        [folder.id]: "",
      }));

      try {
        if (options.deleteFiles) {
          setIsDeletingDefaultFolderWithFiles(true);
          for (const mediaId of getFolderMediaIds(folder)) {
            await deleteMediaAsync({
              endpoint: `${import.meta.env.VITE_API_ENDPOINT}/media/delete/${mediaId}`,
              body: {},
            });
          }
        }

        await hideDefaultFolder(folder);
        if (redirectUrl) {
          openFolder(redirectUrl);
        }
        toast({
          title: "Dossier supprimé",
          description: options.deleteFiles
            ? "Le dossier et ses fichiers ont été supprimés."
            : "Le dossier a été retiré. Les fichiers qu’il contenait sont conservés.",
          duration: 3500,
        });
        setFolderToDelete(null);
        setDeleteRedirectUrl(null);
        setIsDeletingDefaultFolderWithFiles(false);
      } catch (_error) {
        setOptimisticFolderLabels((prev) => {
          const next = { ...prev };
          if (previousOptimisticLabel) {
            next[folder.id] = previousOptimisticLabel;
          } else {
            delete next[folder.id];
          }
          return next;
        });
        setIsDeletingDefaultFolderWithFiles(false);
        // Error feedback is already handled by the shared mutation hook.
      }
    }
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete?.id) return;

    const deletedFileId = Number(fileToDelete.id);
    setOptimisticallyDeletedDocumentIds((prev) => new Set(prev).add(deletedFileId));
    const querySnapshots = removeDocumentFromQueries(deletedFileId);

    try {
      await deleteMediaAsync({
        endpoint: `${import.meta.env.VITE_API_ENDPOINT}/media/delete/${deletedFileId}`,
        body: {},
      });
    } catch (_error) {
      restoreDocumentQueries(querySnapshots);
      setOptimisticallyDeletedDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(deletedFileId);
        return next;
      });
    }
  };

  const handleRenameFolder = async () => {
    if (!folderToRename?.deleteFolderTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast({ title: "Le nom du dossier est requis." });
      return;
    }

    try {
      if (folderToRename.deleteFolderTarget.kind === "custom") {
        const folderId = Number(folderToRename.deleteFolderTarget.id ?? 0);
        const treeSnapshots = getDocumentTreeSnapshots();
        if (folderId > 0) {
          renameFolderInTreeQueries(folderId, trimmed);
        }

        await saveFolderAsync({
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
          body: {
            id: folderToRename.deleteFolderTarget.id,
            name: trimmed,
          },
        }).catch((error) => {
          restoreDocumentTreeQueries(treeSnapshots);
          throw error;
        });
        return;
      }

      const folderUrl = folderToRename.url ?? "";
      const [, query = ""] = folderUrl.split("?");
      const params = new URLSearchParams(query);
      const documentTypes = normalizeDocumentTypeIds(params.get("categories"));
      const isPhotoFolder =
        folderToRename.deleteFolderTarget.defaultKey === "photos" ||
        (params.get("types") ?? "")
          .split(",")
          .some((type) => type === "image");

      if (isPhotoFolder) {
        toast({ title: "Le dossier Photos ne peut pas encore etre renomme." });
        return;
      }

      const previousOptimisticLabel = optimisticFolderLabels[folderToRename.id];
      setOptimisticFolderLabels((prev) => ({
        ...prev,
        [folderToRename.id]: trimmed,
      }));

      try {
        await saveFolderAsync({
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
          body: {
            scopeType: folderToRename.deleteFolderTarget.scopeType,
            scopeId: folderToRename.deleteFolderTarget.scopeId,
            parentId: null,
            name: trimmed,
            defaultKey: folderToRename.deleteFolderTarget.defaultKey,
            reuseByDefaultKey: true,
            ...(documentTypes.length > 0 ? { documentTypes } : {}),
          },
        });

        await saveFolderAsync({
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/default/hide`,
          body: {
            scopeType: folderToRename.deleteFolderTarget.scopeType,
            scopeId: folderToRename.deleteFolderTarget.scopeId,
            defaultKey: folderToRename.deleteFolderTarget.defaultKey,
          },
        });
      } catch (error) {
        setOptimisticFolderLabels((prev) => {
          const next = { ...prev };
          if (previousOptimisticLabel) {
            next[folderToRename.id] = previousOptimisticLabel;
          } else {
            delete next[folderToRename.id];
          }
          return next;
        });
        throw error;
      }
    } catch (_error) {
      // Error feedback is already handled by the shared mutation hook.
    }
  };

  const canDeleteFiles = hasAccessToEdit && !hidePostActions;

  const handleDeleteDocument = async (id: number) => {
    if (!id || deletingDocumentId) return;

    setDeletingDocumentId(id);
    setOptimisticallyDeletedDocumentIds((prev) => new Set(prev).add(Number(id)));
    const querySnapshots = removeDocumentFromQueries(Number(id));

    try {
      const res = await postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/media/delete/${id}`,
        data: {},
      });

      if (
        res !== undefined &&
        res !== null &&
        res.success !== undefined &&
        res.success !== null &&
        res.success
      ) {
        try {
          await onDelete?.(Number(id));
        } catch {
          // The server deletion succeeded; the cache refresh below remains authoritative.
        }
        await refreshDocumentQueries();
      } else {
        restoreDocumentQueries(querySnapshots);
        setOptimisticallyDeletedDocumentIds((prev) => {
          const next = new Set(prev);
          next.delete(Number(id));
          return next;
        });
      }
    } catch (_error) {
      restoreDocumentQueries(querySnapshots);
      setOptimisticallyDeletedDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(Number(id));
        return next;
      });
    } finally {
      setDeletingDocumentId(null);
    }
  };
  const handleCopyLink = async (url: string) => {
    try {
      await copyDocumentLink(url);
      toast({ title: "Lien copié" });
    } catch {
      toast({ title: "Impossible de copier le lien", variant: "destructive" });
    }
  };
  const handleDownload = async (url: string, filename: string) => {
    const downloadToast = showDownloadInProgressToast({
      filename,
      kind: "file",
    });

    try {
      await downloadDocumentFile(url, filename);
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename, kind: "file" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename, kind: "file" });
    }
  };
  const handleDownloadFolder = async (folder: TreeNodeWithPath | null) => {
    if (!folder || downloadingFolderId) return;

    const folderDocuments = getFolderDocuments(folder);
    if (folderDocuments.length === 0) {
      toast({ title: "Ce dossier ne contient aucun fichier à télécharger." });
      return;
    }

    setDownloadingFolderId(folder.id);
    const downloadToast = showDownloadInProgressToast({
      filename: folder.label,
      kind: "folder",
    });

    try {
      await downloadDocumentFolder({
        documents: folderDocuments,
        basePath: folder.fullPath,
        filename: folder.label,
      });
      downloadToast.dismiss();
      showDownloadSuccessToast({ filename: folder.label, kind: "folder" });
    } catch {
      downloadToast.dismiss();
      showDownloadErrorToast({ filename: folder.label, kind: "folder" });
    } finally {
      setDownloadingFolderId(null);
    }
  };
  const folderDetailsStats = getFolderStats(folderToShowDetails);
  const hasDetailsPanel = Boolean(documentToShowDetails || folderToShowDetails);
  const closeDetailsPanel = () => {
    setDocumentToShowDetails(null);
    setFolderToShowDetails(null);
  };
  const openDocumentDetails = (doc: ExplorerDocument) => {
    setFolderToShowDetails(null);
    setDocumentToShowDetails(doc);
  };
  const openFolderDetails = (folder: TreeNodeWithPath | null) => {
    setDocumentToShowDetails(null);
    setFolderToShowDetails(folder);
  };

  useEffect(() => {
    const updateExplorerLayoutHeight = () => {
      const top = explorerLayoutRef.current?.getBoundingClientRect().top ?? 0;
      const availableHeight = window.innerHeight - top - 16;
      setExplorerLayoutHeight(`${Math.max(availableHeight, 520)}px`);
    };

    updateExplorerLayoutHeight();
    window.addEventListener("resize", updateExplorerLayoutHeight);

    return () => {
      window.removeEventListener("resize", updateExplorerLayoutHeight);
    };
  }, [hasDetailsPanel]);

  return (
    <>
      <div
        ref={explorerLayoutRef}
        style={hasDetailsPanel ? { height: explorerLayoutHeight } : undefined}
      >
      <ResizablePanelGroup
        direction="horizontal"
        className={`${hasDetailsPanel ? "h-full overflow-hidden" : ""} items-stretch gap-0`}
      >
        <ResizablePanel defaultSize={hasDetailsPanel ? 68 : 100} minSize={45}>
          <div className={`${hasDetailsPanel ? "h-full overflow-y-auto" : ""} space-y-6 pr-0 md:pr-4`}>
        {/* {breadcrumbPathLabel ? (
        <div className="rounded-2xl border border-[#e8e2d3] bg-[linear-gradient(135deg,#f8f7f2,#ffffff)] px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b705c]">
            Emplacement actuel
          </p>
          <p className="mt-2 text-sm text-[#1f2937]">
            {breadcrumbPathLabel}
          </p>
        </div>
      ) : null} */}

        {folderCards.length > 0 && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#6b705c]">
                Dossiers
              </h3>
              <span className="text-xs text-muted-foreground">
                {folderCards.length} dossier{folderCards.length > 1 ? "s" : ""}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {folderCards.map((folder) => (
                <div
                  key={folder.id}
                  className="group cursor-pointer rounded-2xl border border-[#e8e2d3] bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#cfd9be] hover:bg-[#fbfbf7]"
                  onClick={() => openFolder(folder.url)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openFolder(folder.url);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-3 text-left">
                      <div className="rounded-2xl bg-[#eef4e8] p-3 text-[#1d5215]">
                        <FolderOpen className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-semibold text-[#1f2937] group-hover:text-[#1d5215]">
                          {folder.label}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {folder.count} element{folder.count > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-[#f3f1ea] hover:text-[#1d5215]"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-44"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openFolderDetails(folder);
                          }}
                        >
                          <Info className="mr-2 h-4 w-4" />
                          Voir les détails
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDownloadFolder(folder);
                          }}
                          disabled={downloadingFolderId === folder.id}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Télécharger
                        </DropdownMenuItem>
                        {folder.deleteFolderTarget ? (
                          <>
                          {canRenameFolder(folder) ? (
                            <DropdownMenuItem
                              onSelect={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                window.setTimeout(() => {
                                  setFolderToRename(folder);
                                  setRenameValue(folder.label);
                                }, 0);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Renommer
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            onSelect={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              window.setTimeout(() => {
                                setFolderToDelete(folder);
                              }, 0);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer
                          </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#6b705c]">
              Fichiers
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {visibleFiles.length} fichier{visibleFiles.length > 1 ? "s" : ""}
              </span>
              {filesToolbar}
            </div>
          </div>

          {visibleFiles.length > 0 ? (
            filesView === "list" ? (
              <div className="rounded-2xl border border-[#e8e2d3] bg-white px-4 py-4 shadow-sm">
                <DocumentList
                  data={visibleFiles}
                  typesDocs={typesDocs}
                  onDelete={handleDeleteDocument}
                  onShowDetails={openDocumentDetails}
                />
              </div>
            ) : filesView === "card" ? (
              <div className="rounded-2xl border border-[#e8e2d3] bg-white px-4 py-4 shadow-sm">
                <DocumentCard
                  data={visibleFiles as any}
                  typesDocs={typesDocs}
                  onDelete={handleDeleteDocument}
                  onShowDetails={openDocumentDetails as any}
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-[#e8e2d3] bg-white px-4 py-4 shadow-sm">
                <div className="flex flex-wrap">
                  {visibleFiles.map((doc) => {
                    const extension = doc.name?.split(".").pop()?.toLowerCase() ?? "";
                    const fileUrl = `${import.meta.env.VITE_IP_API}/media/${doc.path}`;
                    const downloadUrl = `${import.meta.env.VITE_API_ENDPOINT}/media/download/${doc.path}`;

                    return (
                      <div className="w-full p-2 sm:w-1/2 lg:w-1/3 xl:w-1/4" key={doc.id}>
                        {isImageFile(doc.name) ? (
                          <ImageThumbnail
                            url={fileUrl}
                            downloadUrl={downloadUrl}
                            treeFinancer={carrierTree}
                            pj={doc}
                            canDelete={canDeleteFiles}
                            onDelete={handleDeleteDocument}
                            onShowDetails={openDocumentDetails}
                          />
                        ) : extension === "pdf" ? (
                          <PdfThumbnail
                            url={fileUrl}
                            downloadUrl={downloadUrl}
                            extension={extension}
                            treeFinancer={carrierTree}
                            pj={doc}
                            canDelete={canDeleteFiles}
                            onDelete={handleDeleteDocument}
                            onShowDetails={openDocumentDetails}
                          />
                        ) : isOfficeFile(doc.name) ? (
                          <OfficeThumbnail
                            srcUrl={fileUrl}
                            downloadUrl={downloadUrl}
                            extension={extension}
                            treeFinancer={carrierTree}
                            pj={doc}
                            canDelete={canDeleteFiles}
                            onDelete={handleDeleteDocument}
                            onShowDetails={openDocumentDetails}
                          />
                        ) : (
                          <div className="rounded-xl border border-[#f1ede4] bg-[#fcfbf7] p-4">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="rounded-xl bg-[#f5f5f1] p-2 text-[#5f5a49]">
                                {getFileIcon(doc.name)}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-[#1f2937]">
                                  {doc.name}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {formatDocDate(doc)}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex justify-end">
                              {canDeleteFiles ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="rounded-full p-2 text-[#5f5a49] transition-colors hover:bg-[#eef4e8] hover:text-[#1d5215]"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        openDocumentDetails(doc);
                                      }}
                                    >
                                      <Info className="mr-2 h-4 w-4" />
                                      Voir les détails
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDownload(downloadUrl, doc.name);
                                        }}
                                        className="flex items-center"
                                      >
                                        <Download className="mr-2 h-4 w-4" />
                                        Télécharger
                                      </button>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        handleCopyLink(fileUrl);
                                      }}
                                    >
                                      <Link className="mr-2 h-4 w-4" />
                                      Copier le lien
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={(event) => {
                                        event.preventDefault();
                                        handleDeleteDocument(Number(doc.id));
                                      }}
                                      disabled={deletingDocumentId === Number(doc.id)}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    title="Voir les détails"
                                    aria-label="Voir les détails"
                                    onClick={() => openDocumentDetails(doc)}
                                    className="rounded-full p-2 text-[#5f5a49] transition-colors hover:bg-[#eef4e8] hover:text-[#1d5215]"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Télécharger"
                                    aria-label="Télécharger"
                                    onClick={() => handleDownload(downloadUrl, doc.name)}
                                    className="rounded-full p-2 text-[#5f5a49] transition-colors hover:bg-[#eef4e8] hover:text-[#1d5215]"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Copier le lien"
                                    aria-label="Copier le lien"
                                    onClick={() => handleCopyLink(fileUrl)}
                                    className="rounded-full p-2 text-[#5f5a49] transition-colors hover:bg-[#eef4e8] hover:text-[#1d5215]"
                                  >
                                    <Link className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <div className="rounded-2xl border border-dashed border-[#d8d2c4] bg-white/70 px-6 py-10 text-center">
              <p className="text-sm font-medium text-[#1f2937]">
                Aucun fichier à ce niveau
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Ouvrez un dossier ou ajustez les filtres pour afficher des fichiers.
              </p>
            </div>
          )}
        </section>
          </div>
        </ResizablePanel>

        {hasDetailsPanel ? (
          <>
            <ResizableHandle withHandle className="mx-1 bg-transparent" />
            <ResizablePanel defaultSize={32} minSize={24} maxSize={45}>
              <aside className="flex h-full flex-col overflow-hidden rounded-md border border-[#dbe6ef] bg-white shadow-sm">
                <div className="shrink-0 flex items-center justify-between border-b border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#5f7180]">
                    Détails
                  </p>
                  <button
                    type="button"
                    aria-label="Fermer les détails"
                    onClick={closeDetailsPanel}
                    className="rounded-md p-2 text-[#5f7180] transition-colors hover:bg-[#eef4e8] hover:text-[#1d5215]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {documentToShowDetails ? (
                    <DocumentDetailsContent pj={documentToShowDetails} treeFinancer={carrierTree} />
                  ) : (
                    <FolderDetailsContent
                      folder={folderToShowDetails}
                      fileCount={folderDetailsStats.fileCount}
                      folderCount={folderDetailsStats.folderCount}
                    />
                  )}
                </div>
              </aside>
            </ResizablePanel>
          </>
        ) : null}
      </ResizablePanelGroup>
      </div>

      <Dialog
        open={Boolean(folderToDelete)}
        onOpenChange={(open) => {
          if (!open && !isFolderDeleteInProgress) {
            setFolderToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Supprimer ce dossier ?</DialogTitle>
            <DialogDescription>
              {folderToDelete?.deleteFolderTarget?.kind === "default" ? (
                <>
                  Choisissez si vous voulez seulement retirer le dossier{" "}
                  <span className="font-medium text-foreground">
                    {folderToDelete?.label}
                  </span>{" "}
                  de l’arborescence, ou supprimer aussi les fichiers qu’il contient.
                  {isDeletingDefaultFolderWithFiles ? (
                    <span className="mt-2 block font-medium text-[#b45309]">
                      Suppression des fichiers en cours...
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  Le dossier{" "}
                  <span className="font-medium text-foreground">
                    {folderToDelete?.label}
                  </span>{" "}
                  sera retire de l’arborescence apres confirmation.
                  {isDeletingFolder ? (
                    <span className="mt-2 flex items-center gap-2 font-medium text-[#b45309]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Suppression du dossier en cours...
                    </span>
                  ) : null}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFolderToDelete(null)}
              disabled={isFolderDeleteInProgress}
            >
              Annuler
            </Button>
            {folderToDelete?.deleteFolderTarget?.kind === "default" ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (folderToDelete) {
                      void handleDeleteFolder(folderToDelete, { deleteFiles: false });
                    }
                  }}
                  disabled={isFolderDeleteInProgress}
                >
                  {isDeletingFolder || isSavingFolder ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Suppression...
                    </span>
                  ) : (
                    "Supprimer seulement le dossier"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    if (folderToDelete) {
                      void handleDeleteFolder(folderToDelete, { deleteFiles: true });
                    }
                  }}
                  disabled={isFolderDeleteInProgress}
                >
                  {isDeletingDefaultFolderWithFiles ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Suppression en cours...
                    </span>
                  ) : (
                    "Supprimer dossier et fichiers"
                  )}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (folderToDelete) {
                    void handleDeleteFolder(folderToDelete);
                  }
                }}
                disabled={isFolderDeleteInProgress}
              >
                {isDeletingFolder ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Suppression en cours...
                  </span>
                ) : (
                  "Supprimer"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(folderToRename)}
        onOpenChange={(open) => {
          if (!open) {
            setFolderToRename(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Renommer le dossier</DialogTitle>
            <DialogDescription>
              Modifiez le nom du dossier sélectionné.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Nom du dossier</label>
            <Input
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              placeholder="Nom du dossier"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFolderToRename(null);
                setRenameValue("");
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleRenameFolder}
              disabled={isSavingFolder || isDeletingFolder}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(fileToDelete)}
        onOpenChange={(open) => {
          if (!open && !isDeletingMedia) {
            setFileToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Supprimer ce fichier ?</DialogTitle>
            <DialogDescription>
              Le fichier{" "}
              <span className="font-medium text-foreground">
                {fileToDelete?.name ?? "sélectionné"}
              </span>{" "}
              sera supprimé définitivement.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFileToDelete(null)}
              disabled={isDeletingMedia}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                void handleDeleteFile();
              }}
              disabled={isDeletingMedia}
            >
              {isDeletingMedia ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentFolderExplorer;
